/**
 * CoachDIA Discovery — Dashboard Admin Overview.
 *
 * Vue unifiée : Viralité (LinkedIn) · B2B Pipeline · Discovery Funnel
 * · Synthèse stratégique (friction / levier / insight).
 *
 * Objectif : piloter la performance Discovery en 30 secondes.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, TrendingDown, Minus, Share2, Users, Target,
  AlertTriangle, Zap, Lightbulb, Loader2, RefreshCw,
  Briefcase, MousePointer2, ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const API = getApiUrl();

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const trendIcon = (arrow) => {
  if (arrow === 'up')   return <TrendingUp className="w-4 h-4 text-[#2D8659]" />;
  if (arrow === 'down') return <TrendingDown className="w-4 h-4 text-[#C0392B]" />;
  return <Minus className="w-4 h-4 text-stone-400" />;
};

// ─── Metric Tile ───
const Metric = ({ label, value, subline, accent = '#0F0F10', testid }) => (
  <div className="p-5 border border-stone-200 bg-white rounded-sm" data-testid={testid}>
    <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-1">{label}</div>
    <div className="font-serif text-3xl" style={{ color: accent }}>{value}</div>
    {subline && <div className="text-xs text-stone-500 mt-1">{subline}</div>}
  </div>
);

// ─── Funnel step row ───
const FunnelStep = ({ label, count, rate, isWorst }) => {
  const rateDisplay = rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`;
  const color = isWorst ? '#C0392B' : '#92710A';
  return (
    <div className={`flex items-center gap-3 py-2 ${isWorst ? 'bg-red-50 px-3 rounded-sm' : ''}`}>
      <div className="w-32 text-xs tracking-wider uppercase text-stone-500">{label}</div>
      <div className="flex-1 flex items-center gap-2">
        <div className="font-serif text-lg text-stone-900 w-14 text-right">{count}</div>
        <div className="flex-1 bg-stone-100 h-2 rounded overflow-hidden">
          <div className="h-full" style={{ backgroundColor: color, width: `${Math.min(100, (rate ?? 0) * 100)}%` }} />
        </div>
        <div className="w-12 text-right text-sm font-semibold" style={{ color }}>{rateDisplay}</div>
      </div>
    </div>
  );
};

export default function AdminDiscoveryOverview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days] = useState(30);
  const [kpi, setKpi] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/discovery/overview`, {
        params: { days },
        headers: authHeaders(),
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  };

  const loadKpi = async () => {
    setKpiLoading(true);
    try {
      const r = await axios.get(`${API}/admin/insights/kpi`, {
        params: { range: '30d' },
        headers: authHeaders(),
      });
      setKpi(r.data);
    } catch (e) {
      // KPI B2B ne doit pas bloquer la vue principale
      // Silencieux si 404 (route pas encore dispo sur preview)
    } finally {
      setKpiLoading(false);
    }
  };

  useEffect(() => { load(); loadKpi(); /* eslint-disable-next-line */ }, []);

  const funnelRows = useMemo(() => {
    if (!data?.funnel) return [];
    const u = data.funnel.users_per_step || {};
    const conv = data.funnel.conversion || {};
    const worst = data.funnel.worst_step_key;
    return [
      { label: 'Vue intro', count: u.view_intro, rate: 1, key: null },
      { label: 'Début test', count: u.start_test, rate: conv.view_intro_to_start_test, key: 'view_intro_to_start_test' },
      { label: 'Test complété', count: u.complete_test, rate: conv.start_test_to_complete_test, key: 'start_test_to_complete_test' },
      { label: 'Paywall vu', count: u.view_paywall, rate: conv.complete_test_to_view_paywall, key: 'complete_test_to_view_paywall' },
      { label: 'Click payer', count: u.click_pay, rate: conv.view_paywall_to_click_pay, key: 'view_paywall_to_click_pay' },
      { label: 'Paiement ✓', count: u.payment_success, rate: conv.click_pay_to_payment_success, key: 'click_pay_to_payment_success' },
    ].map(r => ({ ...r, isWorst: r.key === worst }));
  }, [data]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-overview-page">
      {/* Header */}
      <div className="bg-[#FBF9F3] text-[#2A2319] py-4 sm:py-5 border-b border-[#E8E2D2] sticky top-0 z-20 backdrop-blur-sm bg-[#FBF9F3]/95">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Breadcrumb + retour */}
          <Link
            to="/admin/modules"
            data-testid="admin-overview-back-btn"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#6B7280] hover:text-[#92710A] transition-colors mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Retour Dashboard Administration
          </Link>
          <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A]">Administration · Discovery</div>
              <h1 className="font-serif text-xl sm:text-2xl leading-tight">Overview — piloter la performance en 30 secondes</h1>
            </div>
            <Button variant="outline" onClick={load} disabled={loading}
                    className="border-[#92710A] text-[#92710A] hover:bg-[#FAF7EE] rounded-sm"
                    data-testid="admin-overview-refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Actualiser</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        {loading && !data && (
          <div className="flex items-center gap-2 text-stone-500 py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Chargement des métriques...
          </div>
        )}

        {data && (
          <>
            {/* ─── Synthèse stratégique (en premier — c'est le levier décisionnel) ─── */}
            <section data-testid="admin-overview-synthesis">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-3">Synthèse du moment</div>
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="p-5 border-l-4 border-l-[#C0392B]">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-[#C0392B]" />
                    <div className="text-xs tracking-widest uppercase text-[#C0392B] font-semibold">Friction</div>
                  </div>
                  <p className="text-sm text-stone-800 leading-relaxed">{data.synthesis.friction}</p>
                </Card>
                <Card className="p-5 border-l-4 border-l-[#92710A] bg-[#FBF9F5]">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-[#92710A]" />
                    <div className="text-xs tracking-widest uppercase text-[#92710A] font-semibold">Levier prioritaire</div>
                  </div>
                  <p className="text-sm text-stone-800 leading-relaxed">{data.synthesis.lever}</p>
                </Card>
                <Card className="p-5 border-l-4 border-l-[#1E4F8C]">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-[#1E4F8C]" />
                    <div className="text-xs tracking-widest uppercase text-[#1E4F8C] font-semibold">Insight clé</div>
                  </div>
                  <p className="text-sm text-stone-800 leading-relaxed">{data.synthesis.insight}</p>
                </Card>
              </div>
            </section>

            {/* ─── Viralité LinkedIn ─── */}
            <section data-testid="admin-overview-virality">
              <div className="flex items-center gap-2 mb-4">
                <Share2 className="w-4 h-4 text-[#92710A]" />
                <h2 className="font-serif text-xl text-stone-900">Viralité LinkedIn</h2>
                <div className="flex items-center gap-1 text-xs text-stone-500 ml-2">
                  {trendIcon(data.virality.trend_arrow)}
                  {data.virality.trend_7d_pct !== null
                    ? `${data.virality.trend_7d_pct > 0 ? '+' : ''}${data.virality.trend_7d_pct}% vs 7j précédents`
                    : 'Nouvelle période'}
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-3">
                <Metric label="Aujourd'hui" value={data.virality.cards_today} testid="metric-cards-today" />
                <Metric label="7 jours" value={data.virality.cards_7d} testid="metric-cards-7d" />
                <Metric label="30 jours" value={data.virality.cards_30d} testid="metric-cards-30d" />
                <Metric label="Ratio partage"
                        value={data.virality.share_ratio_pct !== null ? `${data.virality.share_ratio_pct}%` : '—'}
                        subline={`${data.virality.unique_downloaders_30d}/${data.virality.paid_profiles_30d} profils payés ont partagé`}
                        accent="#92710A"
                        testid="metric-share-ratio" />
              </div>
            </section>

            {/* ─── B2B Pipeline ─── */}
            <section data-testid="admin-overview-b2b">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-[#92710A]" />
                <h2 className="font-serif text-xl text-stone-900">B2B Pipeline</h2>
                <a href="/admin/b2b-leads" className="ml-auto text-xs text-[#92710A] hover:underline" data-testid="admin-overview-b2b-leads-link">
                  Gérer les leads →
                </a>
              </div>
              <div className="grid md:grid-cols-4 gap-3 mb-4">
                <Metric label="Leads (7j)" value={data.b2b_pipeline.leads_7d} testid="metric-leads-7d" />
                <Metric label="Leads (30j)" value={data.b2b_pipeline.leads_30d} testid="metric-leads-30d" />
                <Metric label="Leads total" value={data.b2b_pipeline.leads_total} testid="metric-leads-total" />
                <Metric label="Taux contact multi-visites"
                        value={data.b2b_pipeline.contact_rate_pct !== null ? `${data.b2b_pipeline.contact_rate_pct}%` : '—'}
                        subline={`${data.b2b_pipeline.leads_multi_interactions} leads avec > 1 interaction`}
                        accent="#92710A"
                        testid="metric-contact-rate" />
              </div>

              {/* Segmentation */}
              {(data.b2b_pipeline.segmentation_team_size?.length > 0 || data.b2b_pipeline.segmentation_role?.length > 0) && (
                <div className="grid md:grid-cols-2 gap-3">
                  <Card className="p-5">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-3">Taille d'équipe (30j)</div>
                    <div className="space-y-2">
                      {data.b2b_pipeline.segmentation_team_size.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-stone-700">{s.team_size}</span>
                          <span className="font-semibold text-[#0F0F10]">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                  <Card className="p-5">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-3">Rôle (30j)</div>
                    <div className="space-y-2">
                      {data.b2b_pipeline.segmentation_role.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-stone-700">{s.role}</span>
                          <span className="font-semibold text-[#0F0F10]">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              )}
            </section>

            {/* ─── Discovery Funnel ─── */}
            <section data-testid="admin-overview-funnel">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-[#92710A]" />
                <h2 className="font-serif text-xl text-stone-900">Funnel Discovery · {data.funnel_period_days}j</h2>
                {data.funnel.worst_step_label && (
                  <div className="text-xs text-[#C0392B] ml-3 font-semibold">
                    ↓ Drop : {data.funnel.worst_step_label} ({Math.round((data.funnel.worst_step_rate || 0) * 100)}%)
                  </div>
                )}
              </div>
              <Card className="p-5">
                <div className="space-y-1">
                  {funnelRows.map((r, i) => (
                    <FunnelStep key={i} {...r} />
                  ))}
                </div>
                {data.funnel.conversion?.overall_view_to_payment !== null && (
                  <div className="mt-4 pt-4 border-t border-stone-200 flex items-center justify-between">
                    <span className="text-xs tracking-widest uppercase text-stone-500">Conversion globale vue → paiement</span>
                    <span className="font-serif text-2xl text-[#0F0F10]">
                      {Math.round((data.funnel.conversion.overall_view_to_payment || 0) * 100)}%
                    </span>
                  </div>
                )}
              </Card>
            </section>

            {/* ─── 📊 P2 Iter239 — COACHDIA Prism & Pulse KPIs ─── */}
            <section data-testid="admin-overview-insights-b2b">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-4 h-4 text-[#92710A]" />
                <h2 className="font-serif text-xl text-stone-900">COACHDIA Prism & Pulse · 30j</h2>
                <span className="text-xs text-stone-400 ml-2">
                  Tracking post-finalize · segmenté sponsored / B2C
                </span>
                {kpiLoading && <Loader2 className="w-3 h-3 animate-spin ml-1 text-stone-400" />}
              </div>

              {!kpi && !kpiLoading && (
                <Card className="p-5 text-sm text-stone-500" data-testid="insights-b2b-empty">
                  Aucune donnée de tracking disponible pour le moment (aucun email de parcours envoyé sur les 30 derniers jours).
                </Card>
              )}

              {kpi && (
                <>
                  <div className="grid md:grid-cols-4 gap-3 mb-4">
                    <Metric label="Emails envoyés"
                            value={kpi.overall.emails_sent}
                            subline={`Sponsored: ${kpi.sponsored.emails_sent} · B2C: ${kpi.b2c.emails_sent}`}
                            testid="metric-insights-sent" />
                    <Metric label="Clics"
                            value={kpi.overall.clicks}
                            subline={`Sponsored: ${kpi.sponsored.clicks} · B2C: ${kpi.b2c.clicks}`}
                            accent="#92710A"
                            testid="metric-insights-clicks" />
                    <Metric label="CTR"
                            value={`${kpi.overall.ctr}%`}
                            subline={`Sponsored: ${kpi.sponsored.ctr}% · B2C: ${kpi.b2c.ctr}%`}
                            testid="metric-insights-ctr" />
                    <Metric label="Tests finalisés"
                            value={kpi.overall.tests_finalized}
                            subline={`Sponsored: ${kpi.sponsored.tests_finalized} · B2C: ${kpi.b2c.tests_finalized}`}
                            testid="metric-insights-tests" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-3 mb-4">
                    <Card className="p-5" data-testid="insights-b2b-conversion">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-3">
                        Conversion click → booking (confirmed)
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs tracking-wider uppercase text-stone-500 mb-1">7 jours</div>
                          <div className="font-serif text-2xl text-[#0F0F10]">
                            {kpi.overall.conversion_booking_7d}%
                          </div>
                          <div className="text-[11px] text-stone-500 mt-1">
                            Sponsored {kpi.sponsored.conversion_booking_7d}% · B2C {kpi.b2c.conversion_booking_7d}%
                          </div>
                        </div>
                        <div>
                          <div className="text-xs tracking-wider uppercase text-stone-500 mb-1">30 jours</div>
                          <div className="font-serif text-2xl text-[#0F0F10]">
                            {kpi.overall.conversion_booking_30d}%
                          </div>
                          <div className="text-[11px] text-stone-500 mt-1">
                            Sponsored {kpi.sponsored.conversion_booking_30d}% · B2C {kpi.b2c.conversion_booking_30d}%
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-5" data-testid="insights-b2b-top-coaches">
                      <div className="flex items-center gap-2 mb-3">
                        <MousePointer2 className="w-3.5 h-3.5 text-[#92710A]" />
                        <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400">
                          Top 3 coachs cliqués (overall)
                        </div>
                      </div>
                      {(kpi.overall.top_clicked_coaches || []).length === 0 && (
                        <div className="text-sm text-stone-500">Aucun clic tracé pour le moment.</div>
                      )}
                      <div className="space-y-2">
                        {(kpi.overall.top_clicked_coaches || []).map((c, i) => (
                          <div key={i} className="flex items-center justify-between text-sm border-b border-stone-100 last:border-0 pb-1">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 bg-[#FBF8F1] text-[#92710A] text-xs flex items-center justify-center rounded-full font-semibold">
                                {c.rank_position ?? '—'}
                              </span>
                              <span className="text-stone-700 font-mono text-xs">{c.target_coach_id}</span>
                            </div>
                            <span className="font-semibold text-[#0F0F10]">{c.clicks} clics</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                </>
              )}
            </section>

            <div className="text-center text-xs text-stone-400 pt-4">
              Généré le {new Date(data.generated_at).toLocaleString('fr-FR')} — Période funnel : {data.funnel_period_days} jours
            </div>
          </>
        )}
      </div>
    </div>
  );
}
